import { isValidStateTransition } from '../utils/speed-dialer-validation';
import {
  SpeedDialerState,
  SpeedDialerStatus,
  SpeedDialerAction,
} from '../types/speed-dialer.type';

export const initialState: SpeedDialerState = {
  status: 'idle',
  queue: [],
  currentIndex: 0,
  currentPhoneIndex: 0,
  currentConversationId: undefined,
  currentPhoneNumber: undefined,
  currentAttempts: 0,
  maxAttempts: 1,
};

/**
 * Helper function to safely transition state status
 */
const transitionStatus = (
  currentStatus: SpeedDialerStatus,
  nextStatus: SpeedDialerStatus
): SpeedDialerStatus => {
  if (currentStatus === nextStatus) {
    return currentStatus;
  }

  if (isValidStateTransition(currentStatus, nextStatus)) {
    return nextStatus;
  }

  console.warn(
    `Invalid state transition from ${currentStatus} to ${nextStatus}`
  );
  return currentStatus;
};

/**
 * Reducer for managing speed dialer state
 */
export const speedDialerReducer = (
  state: SpeedDialerState = initialState,
  action: SpeedDialerAction
): SpeedDialerState => {
  switch (action.type) {
    case 'START_SESSION': {
      if (state.status !== 'idle') {
        return state;
      }

      const maxAttempts = Math.max(1, Math.floor(action.payload.maxAttempts));
      return {
        ...state,
        queue: action.payload.queue,
        maxAttempts: maxAttempts,
        status: transitionStatus(state.status, 'active'),
        currentIndex: 0,
        currentPhoneIndex: 0,
        currentConversationId: undefined,
        currentPhoneNumber: undefined,
        currentAttempts: 0,
      };
    }

    case 'PAUSE_SESSION': {
      return {
        ...state,
        status: transitionStatus(state.status, 'paused'),
      };
    }

    case 'RESUME_SESSION': {
      return {
        ...state,
        status: transitionStatus(state.status, 'processing_call'),
      };
    }

    case 'STOP_SESSION': {
      return {
        ...initialState,
      };
    }

    case 'CALL_STARTED': {
      const { conversationId, phoneNumber } = action.payload;
      return {
        ...state,
        status: transitionStatus(state.status, 'call_in_progress'),
        currentConversationId: conversationId,
        currentPhoneNumber: phoneNumber,
      };
    }

    case 'CALL_ENDED': {
      if (state.status === 'paused') {
        return state;
      }

      const hasRemainingAttempts =
        state.currentAttempts < state.maxAttempts - 1;
      const currentItem = state.queue[state.currentIndex];
      const nextPhoneIndex = state.currentPhoneIndex + 1;
      const nextConversationIndex = state.currentIndex + 1;
      const hasMorePhoneNumbers =
        currentItem && nextPhoneIndex < currentItem.phoneNumbers.length;
      const hasMoreConversations = nextConversationIndex < state.queue.length;

      if (hasRemainingAttempts) {
        return {
          ...state,
          status: transitionStatus(state.status, 'processing_call'),
          currentAttempts: state.currentAttempts + 1,
        };
      }

      if (hasMorePhoneNumbers) {
        return {
          ...state,
          status: transitionStatus(state.status, 'active'),
          currentPhoneIndex: nextPhoneIndex,
          currentPhoneNumber: undefined,
          currentAttempts: 0,
        };
      }

      if (hasMoreConversations) {
        return {
          ...state,
          status: transitionStatus(state.status, 'active'),
          currentIndex: nextConversationIndex,
          currentPhoneIndex: 0,
          currentConversationId: undefined,
          currentPhoneNumber: undefined,
          currentAttempts: 0,
        };
      }

      return { ...initialState };
    }

    case 'PROCESS_NEXT_CALL': {
      if (state.queue.length === 0) {
        return { ...initialState };
      }
      const currentItem = state.queue[state.currentIndex];
      if (!currentItem) {
        console.warn('No current item found in queue', state);
        return { ...initialState };
      }
      if (state.currentPhoneIndex >= currentItem.phoneNumbers.length) {
        const nextIndex = state.currentIndex + 1;
        if (nextIndex >= state.queue.length) {
          return { ...initialState };
        }
        return {
          ...state,
          currentIndex: nextIndex,
          currentPhoneIndex: 0,
          currentConversationId: undefined,
          currentPhoneNumber: undefined,
          currentAttempts: 0,
          status: transitionStatus(state.status, 'processing_call'),
        };
      }
      const phoneNumber = currentItem.phoneNumbers[state.currentPhoneIndex];
      return {
        ...state,
        currentConversationId: currentItem.conversationId,
        currentPhoneNumber: phoneNumber,
        currentAttempts: 0,
        status: transitionStatus(state.status, 'processing_call'),
      };
    }

    case 'UPDATE_PHONE_NUMBER': {
      const { newPhone } = action.payload;
      const updatedQueue = state.queue.map((item, index) => {
        if (index === state.currentIndex) {
          const updatedPhoneNumbers = [...item.phoneNumbers];
          updatedPhoneNumbers[state.currentPhoneIndex] = newPhone;
          return { ...item, phoneNumbers: updatedPhoneNumbers };
        }
        return item;
      });
      return {
        ...state,
        queue: updatedQueue,
        currentPhoneNumber: newPhone,
      };
    }

    case 'SKIP_CALL': {
      const currentItem = state.queue[state.currentIndex];
      if (!currentItem) {
        return { ...initialState };
      }

      const nextPhoneIndex = state.currentPhoneIndex + 1;
      const nextConversationIndex = state.currentIndex + 1;
      const hasMorePhoneNumbers =
        nextPhoneIndex < currentItem.phoneNumbers.length;
      const hasMoreConversations = nextConversationIndex < state.queue.length;

      if (hasMorePhoneNumbers) {
        return {
          ...state,
          status: transitionStatus(state.status, 'active'),
          currentPhoneIndex: nextPhoneIndex,
          currentPhoneNumber: undefined,
          currentAttempts: 0,
        };
      }

      if (hasMoreConversations) {
        return {
          ...state,
          status: transitionStatus(state.status, 'active'),
          currentIndex: nextConversationIndex,
          currentPhoneIndex: 0,
          currentConversationId: undefined,
          currentPhoneNumber: undefined,
          currentAttempts: 0,
        };
      }

      return { ...initialState };
    }

    default:
      return state;
  }
};
